/**
 * DOM-based in-game HUD: hole info, turn indicator + timer, live scoreboard,
 * emotes, hole-complete and game-over overlays with rematch.
 */
import type Phaser from 'phaser';
import { net, type RoomStateView } from '../net.js';
import { Sound } from '../audio.js';

const EMOTES = ['👏', '😅', '🔥', '😎', '😭', '💀', '🎉'];

function el(tag: string, cls?: string, html?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

class Hud {
  private root: HTMLElement | null = null;
  private holeInfo!: HTMLElement;
  private modeEl!: HTMLElement;
  private banner!: HTMLElement;
  private timerFill!: HTMLElement;
  private scoreboard!: HTMLElement;
  private overlay!: HTMLElement;
  private toastEl!: HTMLElement;
  private splashEl!: HTMLElement;
  private chatEl!: HTMLElement;
  private chatLog!: HTMLElement;
  private chatInput!: HTMLInputElement;
  private chatBadge!: HTMLElement;
  private chatOpen = false;
  private chatSeen = new Set<string>();
  private chatUnread = 0;
  private chatInitialized = false;
  private deadline = 0;
  private turnTotalMs = 30000;
  private timerInt: ReturnType<typeof setInterval> | null = null;
  private connEl!: HTMLElement;
  private rematchUnbind: (() => void) | null = null;

  mount(_scene: Phaser.Scene): void {
    if (this.root) this.unmount();
    const root = el('div', 'hud');
    this.root = root;

    const top = el('div', 'hud-top');
    this.holeInfo = el('div', 'hud-hole', 'Hole');
    this.modeEl = el('div', 'hud-mode', 'Direct');
    top.append(this.holeInfo, this.modeEl);

    this.banner = el('div', 'hud-banner', '');
    const timer = el('div', 'hud-timer');
    this.timerFill = el('div', 'hud-timer-fill');
    timer.append(this.timerFill);

    this.scoreboard = el('div', 'hud-scoreboard');

    const emotes = el('div', 'hud-emotes');
    EMOTES.forEach((em) => {
      const b = el('button', 'hud-emote', em);
      b.onclick = () => {
        net.send('emote', { emote: em });
        Sound.ui();
      };
      emotes.append(b);
    });

    this.toastEl = el('div', 'hud-toast');
    this.overlay = el('div', 'hud-overlay hidden');
    this.splashEl = el('div', 'hud-splash hidden');
    this.connEl = el('div', 'hud-conn hidden', '');

    root.append(top, this.banner, timer, this.scoreboard, emotes, this.toastEl, this.overlay, this.splashEl, this.connEl, this.buildChat());
    document.body.append(root);

    this.timerInt = setInterval(() => this.tickTimer(), 100);
  }

  unmount(): void {
    if (this.timerInt) clearInterval(this.timerInt);
    this.timerInt = null;
    this.rematchUnbind?.();
    this.rematchUnbind = null;
    this.root?.remove();
    this.root = null;
    this.chatSeen.clear();
    this.chatInitialized = false;
    this.chatOpen = false;
    this.chatUnread = 0;
  }

  /** Persistent connection indicator ("Reconnecting…"). */
  setConnection(state: 'ok' | 'reconnecting'): void {
    if (!this.connEl) return;
    if (state === 'ok') {
      this.connEl.classList.add('hidden');
    } else {
      this.connEl.innerHTML = '<span class="spinner"></span> Connection lost — reconnecting…';
      this.connEl.classList.remove('hidden');
    }
  }

  /** Terminal overlay when the seat could not be recovered. */
  showConnectionLost(): void {
    this.setConnection('ok');
    this.overlay.className = 'hud-overlay';
    this.overlay.innerHTML = `
      <div class="hud-card">
        <h2>Connection lost</h2>
        <p class="muted">We couldn't reconnect you to the match.</p>
        <div class="go-actions"><button class="btn primary" id="cl-lobby">Back to lobby</button></div>
      </div>`;
    (this.overlay.querySelector('#cl-lobby') as HTMLButtonElement).onclick = () => {
      net.leave();
      location.reload();
    };
  }

  // ── Chat ───────────────────────────────────────────────────────
  private buildChat(): HTMLElement {
    const chat = el('div', 'hud-chat collapsed');
    const toggle = el('button', 'hud-chat-toggle', '💬');
    this.chatBadge = el('span', 'hud-chat-badge hidden', '');
    toggle.append(this.chatBadge);
    toggle.onclick = () => this.toggleChat();

    const panel = el('div', 'hud-chat-panel');
    this.chatLog = el('div', 'hud-chat-log');
    const inputRow = el('div', 'hud-chat-input');
    this.chatInput = el('input') as HTMLInputElement;
    this.chatInput.type = 'text';
    this.chatInput.maxLength = 200;
    this.chatInput.placeholder = 'Message…';
    const sendBtn = el('button', 'btn small', 'Send');
    const send = () => this.sendChat();
    sendBtn.onclick = send;
    this.chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        send();
      }
      e.stopPropagation(); // don't let typing reach the game (e.g. R = rotate mode)
    });
    inputRow.append(this.chatInput, sendBtn);
    panel.append(this.chatLog, inputRow);

    chat.append(toggle, panel);
    this.chatEl = chat;
    return chat;
  }

  private toggleChat(): void {
    this.chatOpen = !this.chatOpen;
    this.chatEl.classList.toggle('collapsed', !this.chatOpen);
    if (this.chatOpen) {
      this.chatUnread = 0;
      this.updateChatBadge();
      this.chatLog.scrollTop = this.chatLog.scrollHeight;
      this.chatInput.focus();
    }
  }

  private sendChat(): void {
    const text = this.chatInput.value.trim();
    if (!text) return;
    this.chatInput.value = '';
    net.send('chat', { text });
    Sound.ui();
  }

  private updateChatBadge(): void {
    if (this.chatUnread > 0) {
      this.chatBadge.textContent = this.chatUnread > 9 ? '9+' : String(this.chatUnread);
      this.chatBadge.classList.remove('hidden');
    } else {
      this.chatBadge.classList.add('hidden');
    }
  }

  /** Append any new chat lines from synced room state (deduped by id). */
  updateChat(view: RoomStateView, myId: string): void {
    if (!this.chatLog) return;
    for (const m of view.chat) {
      if (this.chatSeen.has(m.id)) continue;
      this.chatSeen.add(m.id);
      const line = el('div', m.system ? 'hud-chat-line sys' : 'hud-chat-line');
      if (m.system) {
        line.innerHTML = `<i>${escapeHtml(m.text)}</i>`;
      } else {
        const player = view.players.find((p) => p.sessionId === m.sessionId);
        const dot = player ? `<span class="sb-dot c${player.colorIndex}"></span>` : '';
        const meCls = m.sessionId && m.sessionId === myId ? ' class="me"' : '';
        line.innerHTML = `${dot}<b${meCls}>${escapeHtml(m.name)}:</b> ${escapeHtml(m.text)}`;
      }
      this.chatLog.append(line);
      while (this.chatLog.childElementCount > 60) this.chatLog.firstElementChild?.remove();
      if (this.chatInitialized && !this.chatOpen) this.chatUnread++;
    }
    this.chatInitialized = true;
    this.updateChatBadge();
    if (this.chatOpen) this.chatLog.scrollTop = this.chatLog.scrollHeight;
  }

  setHoleInfo(holeNum: number, holeCount: number, name: string, par: number): void {
    this.holeInfo.innerHTML = `<b>Hole ${holeNum}/${holeCount}</b> · ${name} · Par ${par}`;
  }

  /** A brief, animated "course intro" card shown as a match begins. */
  showCourseIntro(holeNum: number, holeCount: number, name: string, par: number): void {
    if (!this.splashEl) return;
    this.splashEl.className = 'hud-splash';
    this.splashEl.innerHTML = `
      <div class="hud-splash-card">
        <div class="hud-splash-hole">Hole ${holeNum} / ${holeCount}</div>
        <div class="hud-splash-name">${escapeHtml(name)}</div>
        <div class="hud-splash-par">Par ${par}</div>
        <div class="hud-splash-track"><div class="hud-splash-cup"></div><div class="hud-splash-ball"></div></div>
      </div>`;
    window.setTimeout(() => this.splashEl?.classList.add('out'), 1300);
    window.setTimeout(() => { if (this.splashEl) this.splashEl.className = 'hud-splash hidden'; }, 1750);
  }

  setMode(name: string): void {
    this.modeEl.textContent = `Mode: ${name} (R)`;
  }

  updateTurn(view: RoomStateView, myId: string): void {
    this.deadline = view.turnDeadline;
    if (view.turnTimeoutMs > 0) this.turnTotalMs = view.turnTimeoutMs;
    const cur = view.players.find((p) => p.sessionId === view.currentTurn);
    if (view.phase === 'round_over') {
      this.banner.textContent = 'Round over';
      this.banner.className = 'hud-banner';
      return;
    }
    if (!cur) {
      this.banner.textContent = '';
      return;
    }
    if (cur.sessionId === myId) {
      this.banner.textContent = 'Your turn — aim and release to shoot';
      this.banner.className = 'hud-banner your-turn';
    } else {
      this.banner.textContent = `${cur.name}'s turn…`;
      this.banner.className = 'hud-banner';
    }
  }

  private tickTimer(): void {
    if (!this.deadline) {
      this.timerFill.style.width = '0%';
      return;
    }
    const total = this.turnTotalMs;
    const left = Math.max(0, this.deadline - Date.now());
    this.timerFill.style.width = `${Math.min(100, (left / total) * 100)}%`;
    this.timerFill.style.background = left < total * 0.2 ? '#ff6b6b' : '#7fe39a';
  }

  updateScoreboard(view: RoomStateView, myId: string): void {
    const sorted = [...view.players].sort((a, b) => a.strokesTotal - b.strokesTotal);
    this.scoreboard.innerHTML = sorted
      .map((p) => {
        const me = p.sessionId === myId ? ' me' : '';
        const turn = p.sessionId === view.currentTurn ? ' active' : '';
        const dot = `<span class="sb-dot c${p.colorIndex}"></span>`;
        const conn = p.connected || p.isBot ? '' : ' ⚠';
        return `<div class="sb-row${me}${turn}">${dot}<span class="sb-name">${escapeHtml(p.name)}${conn}</span><span class="sb-hole">${p.holed ? '✓' : p.strokesThisHole}</span><span class="sb-total">${p.strokesTotal}</span></div>`;
      })
      .join('');
  }

  toast(msg: string): void {
    this.toastEl.textContent = msg;
    this.toastEl.classList.add('show');
    setTimeout(() => this.toastEl.classList.remove('show'), 2200);
  }

  /** Dismiss any modal card (used when a rematch/new hole begins). */
  hideOverlay(): void {
    this.overlay.className = 'hud-overlay hidden';
  }

  showHoleComplete(payload: unknown): void {
    const data = payload as { scores: Array<{ name: string; score: number; total: number }> };
    const rows = data.scores
      .sort((a, b) => a.total - b.total)
      .map((s) => `<div class="hc-row"><span>${escapeHtml(s.name)}</span><span>+${s.score}</span><span>= ${s.total}</span></div>`)
      .join('');
    this.overlay.className = 'hud-overlay';
    this.overlay.innerHTML = `<div class="hud-card"><h2>Hole complete</h2><div class="hc-list">${rows}</div><p class="muted">Next hole starting…</p></div>`;
    setTimeout(() => this.overlay.classList.add('hidden'), 2400);
  }

  showGameOver(payload: unknown, myId: string): void {
    const data = payload as { winnerId?: string; players: Array<{ sessionId: string; name: string; strokesTotal: number; holeScores: number[] }> };
    const sorted = [...data.players].sort((a, b) => a.strokesTotal - b.strokesTotal);
    const won = data.winnerId === myId;
    const rows = sorted
      .map((p, i) => {
        const crown = i === 0 ? '👑 ' : `${i + 1}. `;
        return `<div class="go-row${p.sessionId === myId ? ' me' : ''}"><span>${crown}${escapeHtml(p.name)}</span><span>${p.strokesTotal}</span></div>`;
      })
      .join('');
    this.overlay.className = 'hud-overlay';
    this.overlay.innerHTML = `
      <div class="hud-card">
        <h2>${won ? '🏆 You win!' : 'Round over'}</h2>
        <div class="go-list">${rows}</div>
        <div class="go-actions">
          <button class="btn primary" id="go-rematch">Rematch</button>
          <button class="btn" id="go-lobby">Back to lobby</button>
        </div>
        <p class="muted" id="go-rematch-status"></p>
      </div>`;
    (this.overlay.querySelector('#go-rematch') as HTMLButtonElement).onclick = () => {
      net.send('rematch');
      (this.overlay.querySelector('#go-rematch-status') as HTMLElement).textContent = 'Waiting for opponent…';
    };
    (this.overlay.querySelector('#go-lobby') as HTMLButtonElement).onclick = () => {
      net.leave();
      location.reload();
    };
    this.rematchUnbind?.();
    this.rematchUnbind = net.on('rematch_vote', (p) => {
      const v = p as { votes: number; needed: number };
      const status = this.overlay.querySelector('#go-rematch-status') as HTMLElement | null;
      if (status) status.textContent = `Rematch: ${v.votes}/${v.needed} ready`;
    });
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);
}

export const hud = new Hud();
